import { getAuth, createUserWithEmailAndPassword, sendEmailVerification, deleteUser, signInWithEmailAndPassword } from "firebase/auth";
import { CreateAccountApi, VerifyAccountApi } from "apis/authServices";
import { useToastStore } from "store/toastStore";




export const handleSendCode = async (
  email: string,
  username: string,
  password: string,
  setError: (message: string) => void,
  setIsEmailVerifying: (status: boolean) => void,
  navigate: (path: string) => void,
  setIsVerified: (state: boolean) => void,
  setIsAuthenticated: (state: boolean) => void,
  // Switches the UI to the "waiting for the user to click the email link" step,
  // instead of blocking-polling here. The account is finished later from that
  // step (completeSignupIfVerified) when the user confirms they've verified.
  setAwaitingVerification: (state: boolean) => void,
  referralCode?: string
) => {
  setError("");
  setIsEmailVerifying(true);
  
  
  
  const trimmedEmail = email.trim();
  const trimmedUsername = username.trim();
  const auth = getAuth();

  if (!trimmedUsername) {
    setError("Please enter your name.");
    setIsEmailVerifying(false);
    return;
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
    setError("Please enter a valid email address.");
    setIsEmailVerifying(false);
    return;
  }

  
  
  try {
      const response = await VerifyAccountApi(trimmedEmail);

      const statusId = response?.statusId ?? null;
      switch (statusId) {
        case 1: // Inactive → Redirect to Create Account
      
        try {
          // Attempt to sign in with provided password
          const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, password);
          const user = userCredential.user;
          const idToken = await user.getIdToken(); // Get Firebase ID token

          useToastStore.getState().showToast(
            "Inactive account detected. Reactivating account...",
            "info"
          );

          // Call CreateAccountApi to reactivate account
          navigate("/createAccount"); 

          
        } catch (signInError: any) {
          setError("Invalid password or account issue. Please try logging in.");
          useToastStore.getState().showToast(
            "Please verify your password or log in.",
            "error",
         
          );
        }
        return;
        case 2: // Active
        useToastStore.getState().showToast(
          'Email already exists . Redirecting to Login...',
          'error'
        );
          setTimeout(() => navigate("/login"), 3000);
          return;
      
        case 3: // Blocked
        useToastStore.getState().showToast(
          response.message,
          'error'
        );
        return;
        case 4: // Deleted
        useToastStore.getState().showToast(
          response.message,
          'error'
        );
          
          return;
      
        case null: // Email does not exist (Status 200 OK)
          break;
      
        default:
          setError("Unexpected response from server.");
          return;
      }
     
      
      const userCredential = await createUserWithEmailAndPassword(auth,  trimmedEmail, password);

      await sendEmailVerification(userCredential.user);

      useToastStore.getState().showToast(
        "Verification email sent! Please check your registered email ID.",
        "success"
      );

          // Hand off to the waiting step — the user clicks the link in their
          // email, then confirms via completeSignupIfVerified(). No blocking
          // poll, so a slow click / closed tab / network blip no longer strands
          // the signup.
          setAwaitingVerification(true);
          return;
        } catch (error: any) {
          if (error.code === "auth/email-already-in-use") {
            // The email exists in Firebase Auth but the backend reported it does
            // NOT exist — i.e. a previous signup created the Firebase user but
            // never finished verifying (a "zombie" unverified account). Instead
            // of bouncing to login (which also fails), recover it using the
            // password the user just entered.
            try {
              const existing = await signInWithEmailAndPassword(
                auth,
                trimmedEmail,
                password,
              );

              if (existing.user.emailVerified) {
                // Verified in Firebase but missing on the backend — just finish
                // creating the backend account.
                const created = await CreateAccountApi();
                if (created) {
                  navigate("/createAccount");
                } else {
                  setError("This email is already registered. Please sign in.");
                  setTimeout(() => navigate("/login"), 2000);
                }
                return;
              }

              // Still unverified — resend the link and continue the same
              // verification flow, reusing the existing account (do NOT delete
              // it on timeout; the user can retry).
              await sendEmailVerification(existing.user);
              useToastStore
                .getState()
                .showToast(
                  "Verification email re-sent! Please check your registered email ID.",
                  "success",
                );

              // Reuse the existing (unverified) account and hand off to the
              // waiting step, same as a fresh signup.
              setAwaitingVerification(true);
              return;
            } catch (signInError: any) {
              // Wrong password (or another issue) → the email belongs to a real
              // account with a different password. Send them to login.
              setError(
                "This email is already registered. Please sign in with your password.",
              );
              setTimeout(() => navigate("/login"), 2000);
            }
          } else {
            setError(error.message || "Error verifying account. Please try again.");
          }
        } finally {
          setIsEmailVerifying(false);
        }
      };

/**
 * Called from the "waiting for verification" step when the user says they've
 * clicked the link. Refreshes the Firebase user and, if the email is now
 * verified, creates the backend account and proceeds. This works no matter how
 * long the user took — there is no expiry window on our side.
 */
export const completeSignupIfVerified = async (
  setError: (message: string) => void,
  navigate: (path: string) => void,
  setIsChecking: (state: boolean) => void,
  // Background auto-checks pass silent=true: if not verified yet, stay quiet
  // (no error, no spinner) instead of nagging the user.
  // guard: a shared { current } flag so the manual button and the auto-detect
  // triggers (interval / focus / visibility) can't create the account twice.
  options?: { silent?: boolean; guard?: { current: boolean } },
) => {
  const silent = options?.silent ?? false;
  const guard = options?.guard;
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    if (!silent) setError("Your session expired. Please start the sign up again.");
    return;
  }

  if (!silent) {
    setIsChecking(true);
    setError("");
  }
  try {
    await user.reload();
    if (!user.emailVerified) {
      if (!silent) {
        setError(
          "Email not verified yet. Open the link we emailed you, then tap “I've verified my email”.",
        );
      }
      return;
    }

    // Verified — create the backend account exactly once even if several
    // triggers fire together.
    if (guard) {
      if (guard.current) return;
      guard.current = true;
    }

    const created = await CreateAccountApi();
    if (created) {
      navigate("/createAccount");
    } else {
      if (guard) guard.current = false; // let the user retry
      if (!silent) setError("Account creation failed. Please try again.");
    }
  } catch (error: any) {
    if (guard) guard.current = false; // reset so a retry can run
    if (!silent) {
      setError(error?.message || "Could not confirm verification. Please try again.");
    }
  } finally {
    if (!silent) setIsChecking(false);
  }
};

/** Re-send the verification email to the pending (still signed-in) user. */
export const resendVerificationEmail = async (
  setError: (message: string) => void,
): Promise<boolean> => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    setError("Your session expired. Please start the sign up again.");
    return false;
  }
  try {
    await sendEmailVerification(user);
    useToastStore
      .getState()
      .showToast("Verification email re-sent! Please check your inbox.", "success");
    return true;
  } catch (error: any) {
    setError(
      error?.code === "auth/too-many-requests"
        ? "Too many attempts. Please wait a minute before resending."
        : error?.message || "Could not resend the email. Please try again.",
    );
    return false;
  }
};

/**
 * Abandon a pending signup ("use a different email"): delete the throwaway
 * Firebase user, but only if it was never verified (so we never delete a real
 * account).
 */
export const cancelPendingSignup = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user && !user.emailVerified) {
    try {
      await deleteUser(user);
    } catch {
      /* best effort — ignore */
    }
  }
};